let checked = false;

const checkToBlob = () => {
  if (checked) return;

  if (HTMLCanvasElement.prototype.toBlob) {
    /* eslint no-console: 0 */
    console.info('It looks like Chrome now supports HTMLCanvasElement.toBlob.. time to uncomment some code!');
  } else {
    checked = true;
    Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
      value: function (callback, type, quality) {

        let binStr = atob(this.toDataURL(type, quality).split(',')[1]),
          len = binStr.length,
          arr = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
          arr[i] = binStr.charCodeAt(i);
        }

        callback(new Blob([arr], {type: type || 'image/png'}));
      }
    });
  }
};

export interface ISize {
  width: number;
  height: number;
}

class ImageProcessor {
  protected _image: HTMLImageElement;
  protected _load: Promise<HTMLImageElement>;
  width: number;
  height: number;
  src: any;

  constructor(src: string) {
    this._image = new Image();
    this._image.src = src;
    this._load = new Promise((res) => {

      this._image.onload = () => {
        this.width = this._image.naturalWidth;
        this.height = this._image.naturalHeight;
        res(this._image);
      };

      this._image.src = src;
    });

    checkToBlob();
  }

  isBigger(size: ISize): Promise<boolean> {
    let {width, height} = size;
    return this._load.then(() => this.width >= width && this.height >= height);
  }

  isEqual(size: ISize): Promise<boolean> {
    let {width, height} = size;
    return this._load.then(() => this.width === width && this.height === height);
  }

  async isSmaller(size: ISize): Promise<boolean> {
    let isBigger = await this.isBigger(size);
    let isEqual = await this.isEqual(size);
    return !isBigger && !isEqual;
  }

  cropPercentage(crop: any, needWidth: number, needHeight: number, format: string = 'image/jpeg'): Promise<any> {
    return this._load.then((image) => {
      let imageWidth = this.width;
      let imageHeight = this.height;

      let cropX = crop.x / 100 * imageWidth;
      let cropY = crop.y / 100 * imageHeight;

      let cropWidth = crop.width / 100 * imageWidth;
      let cropHeight = crop.height / 100 * imageHeight;

      needWidth = needWidth || cropWidth;
      needHeight = needHeight || cropHeight;

      let canvas: HTMLCanvasElement = <HTMLCanvasElement> document.createElement('canvas');
      canvas.width = needWidth;
      canvas.height = needHeight;

      let ctx = canvas.getContext('2d');
      ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, needWidth, needHeight);

      return convertCanvasToBlob(canvas, format);
    });
  }
}

let loadImage = (imageSrc): Promise<HTMLImageElement> =>
  new Promise((res, rej) => {
    let image: HTMLImageElement = new Image();

    image.onload = () => {
      res(image);
    };
    image.onerror = rej;

    image.src = URL.createObjectURL(imageSrc);
  });

let convertCanvasToBlob = (canvas, format = 'image/jpeg') =>
  new Promise(res => {
    canvas.toBlob(res, format);
  });

export async function autoCropImage(imageSrc, needWidth, needHeight, format = 'image/jpeg') {
  let canvas = document.createElement('canvas');
  let src = await loadImage(imageSrc);

  let {naturalHeight, naturalWidth} = src;

  // get min scale
  let scale = Math.min(
    naturalWidth / needWidth,
    naturalHeight / needHeight
  );

  canvas.width = needWidth;
  canvas.height = needHeight;

  let centerShift_x = ( canvas.width - naturalWidth/scale ) / 2;
  let centerShift_y = ( canvas.height - naturalHeight/scale ) / 2;  

  let ctx = canvas.getContext('2d');

  ctx.drawImage(src,
    0,
    0,
    naturalWidth,
    naturalHeight,
    centerShift_x, centerShift_y, naturalWidth / scale, naturalHeight / scale
  ); 

  return await convertCanvasToBlob(canvas, format);
}

export function drawImageFromVideo(src, needWidth = 1920, needHeight = 1080, format = 'image/jpeg') {
  let canvas = <HTMLCanvasElement> document.createElement('canvas');
  let {videoHeight, videoWidth} = src;

  // get min scale
  let scale = Math.min(
    videoWidth / needWidth,
    videoHeight / needHeight
  );

  canvas.width = needWidth;
  canvas.height = needHeight;

  let canvasStartWidth = 0;
  let canvasStartHeight = 0;

  let ctx = canvas.getContext('2d');
  ctx.drawImage(src, canvasStartWidth, canvasStartHeight, videoWidth - canvasStartWidth * 2, videoHeight - canvasStartHeight * 2, 0, 0, needWidth, needHeight);

  return convertCanvasToBlob(canvas, format);
}

export default ImageProcessor;



// WEBPACK FOOTER //
// ./src/utilites/ImageProcessor.ts